import React, { useMemo, useRef, useState, useEffect } from "react";
import { t as tr } from "../i18n";

const API = process.env.REACT_APP_API_URL || "";

// « [01:02.50] texte » → [{ t: 62.5, text }] (même lecture que le serveur).
function parseLrc(text) {
  const out = [];
  for (const raw of String(text || "").split(/\r?\n/)) {
    const stamps = [...raw.matchAll(/\[(\d{1,3}):(\d{1,2}(?:[.:]\d{1,3})?)\]/g)];
    if (!stamps.length) continue;
    const words = raw.replace(/\[[^\]]*\]/g, "").replace(/<\d+:\d+(?:\.\d+)?>/g, "").replace(/\s+/g, " ").trim();
    for (const m of stamps) out.push({ t: parseInt(m[1]) * 60 + parseFloat(m[2].replace(":", ".")), text: words });
  }
  return out.sort((a, b) => a.t - b.t);
}
const mmss = s => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, "0")}`;

// Les paroles minutées d'une musique, pour le Blind Test « Paroles » :
// récupération sur LRCLIB, saisie à la main, décalage réglé à l'oreille grâce
// à l'aperçu, et ligne d'arrêt imposée (sinon le jeu la choisit).
export default function AdminLyrics({ question, mediaUrl, authFetch, flash, onSaved }) {
  const [lrc, setLrc] = useState(question?.lyrics_lrc || "");
  const [offset, setOffset] = useState((question?.lyrics_offset || 0) / 1000);
  const [line, setLine] = useState(Number.isInteger(question?.lyrics_line) ? question.lyrics_line : null);
  const [busy, setBusy] = useState(false);
  const [pos, setPos] = useState(0);
  const audioRef = useRef(null);

  // Le passage d'une fiche à l'autre recharge ses paroles.
  useEffect(() => {
    setLrc(question?.lyrics_lrc || "");
    setOffset((question?.lyrics_offset || 0) / 1000);
    setLine(Number.isInteger(question?.lyrics_line) ? question.lyrics_line : null);
  }, [question?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const lines = useMemo(() => parseLrc(lrc), [lrc]);
  let cur = -1;
  lines.forEach((l, i) => { if (l.t + offset <= pos) cur = i; });

  const fetchLrc = async () => {
    setBusy(true);
    const r = await authFetch(`${API}/admin/questions/${question.id}/lyrics/fetch`, { method: "POST" });
    const d = await r?.json().catch(() => ({}));
    setBusy(false);
    if (!r?.ok) return flash(d?.error || tr("admin.error"), true);
    setLrc(d.lyrics_lrc);
    flash(tr("admin.lyricsFound"));
    onSaved?.();
  };

  const save = async () => {
    setBusy(true);
    const r = await authFetch(`${API}/admin/questions/${question.id}/lyrics`, {
      method: "PUT", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ lyrics_lrc: lrc, lyrics_offset: Math.round(offset * 1000), lyrics_line: line }),
    });
    const d = await r?.json().catch(() => ({}));
    setBusy(false);
    if (!r?.ok) return flash(d?.error || tr("admin.error"), true);
    flash(tr("admin.lyricsSaved"));
    onSaved?.();
  };

  const nudge = d => setOffset(o => Math.round((o + d) * 10) / 10);

  return (
    <div className="adm-answers-section adm-lyrics">
      <h4 className="adm-answers-label">{tr("admin.lyricsTitle")}</h4>
      <p className="adm-answers-hint">{tr("admin.lyricsHint")}</p>
      <div className="adm-lyrics-actions">
        <button className="btn adm-btn-dark" disabled={busy} onClick={fetchLrc}>
          <i className="bi bi-search"></i> {tr("admin.lyricsFetch")}
        </button>
        <button className="btn btn-primary" disabled={busy} onClick={save}>{tr("admin.lyricsSave")}</button>
      </div>
      <textarea className="adm-input adm-lyrics-text" rows={6} value={lrc} disabled={busy}
        placeholder={"[00:16.60] Qui dit étude dit travail\n[00:20.10] Qui dit taf te dit les thunes"}
        onChange={e => setLrc(e.target.value)} />
      {lines.length > 0 && <>
        {/* L'aperçu : la ligne en cours suit la musique, décalage compris. */}
        {mediaUrl && <audio ref={audioRef} controls src={`${API}${mediaUrl}`} className="adm-lyrics-audio"
          onTimeUpdate={e => setPos(e.currentTarget.currentTime)} />}
        <p className="adm-lyrics-now">{cur >= 0 ? lines[cur].text : "♪"}</p>
        <div className="adm-lyrics-row">
          <span>{tr("admin.lyricsOffset")}</span>
          <button className="btn adm-btn-dark" onClick={() => nudge(-0.5)}>−0,5 s</button>
          <input className="adm-settings-input" type="number" step="0.1" value={offset}
            onChange={e => setOffset(parseFloat(e.target.value) || 0)} />
          <button className="btn adm-btn-dark" onClick={() => nudge(0.5)}>+0,5 s</button>
        </div>
        <div className="adm-lyrics-row">
          <span>{tr("admin.lyricsLine")}</span>
          <select className="adm-theme-select" value={line ?? ""} onChange={e => setLine(e.target.value === "" ? null : parseInt(e.target.value))}>
            <option value="">{tr("admin.lyricsLineAuto")}</option>
            {lines.map((l, i) => l.text && <option key={i} value={i}>{mmss(l.t + offset)} — {l.text}</option>)}
          </select>
        </div>
      </>}
    </div>
  );
}
